/**
 * "Reutiliza tus posts" — lets the user take an already-published post and
 * re-publish it on the networks it was NOT yet posted to. Clicking opens a
 * small dialog listing the connected accounts that aren't already targets of
 * this post; picking some and confirming navigates to the composer
 * (/dashboard/posts/new) pre-filled with the same media + caption and those
 * networks pre-selected (see the `reuse` handling in PostCreate).
 *
 * Shared between the Publicaciones list and the calendar preview modal.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Repeat2, X, Check, ArrowRight, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { AccountAvatar } from './AccountAvatar';

type Account = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
  status: 'connected' | 'disconnected';
};

export type ReuseState = {
  reuse: {
    mediaUrls: string[];
    caption: string;
    accountIds: string[];
  };
};

export function ReusePostButton({
  media,
  caption,
  usedAccountIds,
  variant = 'row',
  onBeforeNavigate,
}: {
  media: { url: string; type?: string }[];
  caption: string;
  /** socialAccountId of every network this post already targets. */
  usedAccountIds: string[];
  variant?: 'row' | 'modal';
  /** Called right before navigating away (e.g. to close a modal). */
  onBeforeNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['social', 'accounts'],
    queryFn: () => api<{ accounts: Account[] }>('/api/social/accounts'),
    enabled: open,
  });

  const connected = (data?.accounts ?? []).filter((a) => a.status === 'connected');
  const used = new Set(usedAccountIds);
  const available = connected.filter((a) => !used.has(a.id));

  const [selected, setSelected] = useState<string[]>([]);
  // Default to every available network selected each time the dialog opens.
  useEffect(() => {
    if (open) setSelected(available.map((a) => a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  // Nothing to reuse if the post has neither media nor text.
  if (media.length === 0 && !caption) return null;

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function reuse() {
    if (selected.length === 0) return;
    onBeforeNavigate?.();
    const state: ReuseState = {
      reuse: { mediaUrls: media.map((m) => m.url), caption, accountIds: selected },
    };
    navigate('/dashboard/posts/new', { state });
  }

  const btnClass =
    variant === 'modal'
      ? 'inline-flex items-center justify-center gap-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-100 rounded-md px-2.5 py-1.5 transition'
      : 'inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-100 rounded-md px-2.5 py-1.5 transition';

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={btnClass}
        title="Reutilizar post en otra red"
      >
        <Repeat2 size={13} />
        Reutilizar
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* header */}
              <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="h-8 w-8 rounded-lg bg-violet-50 text-violet-600 inline-flex items-center justify-center">
                    <Repeat2 size={16} />
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 leading-tight">Reutilizar post</h3>
                    <p className="text-[11px] text-slate-500">Publícalo en otras redes</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Cerrar"
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition"
                >
                  <X size={16} />
                </button>
              </div>

              {/* body */}
              <div className="px-5 py-4">
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2 text-slate-400 py-8 text-sm">
                    <Loader2 size={16} className="animate-spin" /> Cargando redes…
                  </div>
                ) : available.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">
                    Este post ya está en todas tus redes conectadas. Conecta otra red para
                    reutilizarlo.
                  </p>
                ) : (
                  <>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
                      Elige dónde publicarlo
                    </p>
                    <ul className="space-y-1">
                      {available.map((a) => {
                        const meta = PLATFORM_META[a.platform];
                        const checked = selected.includes(a.id);
                        return (
                          <li key={a.id}>
                            <button
                              type="button"
                              onClick={() => toggle(a.id)}
                              className={cn(
                                'w-full flex items-center gap-3 px-2.5 py-2 rounded-lg border transition text-left',
                                checked
                                  ? 'border-violet-200 bg-violet-50/60'
                                  : 'border-slate-200 hover:bg-slate-50',
                              )}
                            >
                              <AccountAvatar
                                account={{
                                  platform: a.platform,
                                  username: a.username,
                                  displayName: a.displayName,
                                  profilePhotoUrl: a.profilePhotoUrl,
                                }}
                                size={30}
                              />
                              <span className="flex-1 min-w-0">
                                <span className="block truncate text-sm font-medium text-slate-800">
                                  {a.displayName || a.username || meta?.label || a.platform}
                                </span>
                                <span className="block text-[11px] text-slate-500">
                                  {meta?.label ?? a.platform}
                                </span>
                              </span>
                              <span
                                className={cn(
                                  'h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition',
                                  checked
                                    ? 'bg-violet-600 border-violet-600 text-white'
                                    : 'border-slate-300 bg-white',
                                )}
                              >
                                {checked && <Check size={13} />}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>

              {/* footer */}
              {available.length > 0 && (
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
                  <button
                    type="button"
                    onClick={reuse}
                    disabled={selected.length === 0}
                    className="btn-primary w-full justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reutilizar
                    {selected.length > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/20 text-[11px] font-semibold tabular-nums">
                        {selected.length}
                      </span>
                    )}
                    <ArrowRight size={15} />
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
